import { z } from 'zod'
import { makeFieldKeys, validateWithZodSchema } from '../../core/utils/utilities.ts'

// Read shape of the Phase 3 Bulk Topic Subscription Manager
// (view__bulk_account_topics): the global topic column set ONCE, plus one
// filtered/paginated account per `data` entry — each with its live
// account_topics ids and one row per comms-eligible contact with that
// contact's live contact_account_topics FOR THAT ACCOUNT (eligibility join
// identical to Phase 2 / the Phase 0 propagate trigger). Standard
// {data, has_more, count} list envelope. Validated before it reaches the
// screen (CLAUDE.md: validate every API response).

export const BulkTopicSchema = z
  .object({
    comms_topics__id: z.string(),
    comms_topics__label: z.string()
  })
  .strict()

export const BulkContactSchema = z
  .object({
    contacts__id: z.string(),
    contacts__first_name: z.string().nullable(),
    contacts__last_name: z.string().nullable(),
    contacts__email: z.string().nullable(),
    contact_account_topics__comms_topic_ids: z.array(z.string())
  })
  .strict()

export const BulkAccountSchema = z
  .object({
    accounts__id: z.string(),
    accounts__name: z.string().nullable(),
    accounts__lp_status_id: z.string().nullable(),
    accounts__lp_status_label: z.string().nullable(),
    account_topics__comms_topic_ids: z.array(z.string()),
    contacts: z.array(BulkContactSchema)
  })
  .strict()

export const BulkAccountTopicsModelSchema = z
  .object({
    comms_topics: z.array(BulkTopicSchema),
    data: z.array(BulkAccountSchema),
    has_more: z.boolean(),
    count: z.number()
  })
  .strict()

export const BulkFilterOptionSchema = z
  .object({
    value: z.string(),
    label: z.string()
  })
  .strict()

export const BulkFilterOptionsModelSchema = z
  .object({
    roles: z.array(BulkFilterOptionSchema),
    funds: z.array(BulkFilterOptionSchema),
    lp_status: z.array(BulkFilterOptionSchema)
  })
  .strict()

export const BulkAccountTopicsModelFields = makeFieldKeys(BulkAccountTopicsModelSchema)

export type BulkTopic = z.infer<typeof BulkTopicSchema>
export type BulkContact = z.infer<typeof BulkContactSchema>
export type BulkAccount = z.infer<typeof BulkAccountSchema>
export type BulkAccountTopicsModel = z.infer<typeof BulkAccountTopicsModelSchema>
export type BulkFilterOption = z.infer<typeof BulkFilterOptionSchema>
export type BulkFilterOptionsModel = z.infer<typeof BulkFilterOptionsModelSchema>

export function validateBulkAccountTopicsModel(data: unknown): BulkAccountTopicsModel {
  try {
    return validateWithZodSchema(BulkAccountTopicsModelSchema, data, 'bulk account topics model')
  } catch (error) {
    throw new Error(`Bulk account topics model validation failed: ${error}`)
  }
}

export function validateBulkFilterOptionsModel(data: unknown): BulkFilterOptionsModel {
  try {
    return validateWithZodSchema(BulkFilterOptionsModelSchema, data, 'bulk filter options model')
  } catch (error) {
    throw new Error(`Bulk filter options model validation failed: ${error}`)
  }
}
